import i18next from 'i18next';
import { initReactI18next, Trans as TransRaw, useTranslation as useTranslationRaw } from 'react-i18next';
import Starling from '@ies/starling_client';
import { reportDependencyResult } from '@/monitoring';
import { REGION_CONFIG } from './region-config';

export const i18n = i18next;
export const useTranslation = useTranslationRaw;
export const Trans = TransRaw;

const EMAIL_NAMESPACE = 'i18n_ecom_shop_email';
const STARLING_API_KEY = 'a8a6b4604e0011f199bc633894fd0b73';
const STARLING_PROJECT_ID = 52915;
const STARLING_NAMESPACE = 'fe';
const STARLING_ZONE_HOST = 'https://starling-oversea.byteoversea.com';
const STARLING_TEA_CHANNEL_DOMAIN = 'https://maliva-mcs.byteoversea.com';

const defaultLanguage = REGION_CONFIG.language.defaultLang || 'en';
const supportedLngs = Array.from(new Set([...(REGION_CONFIG.language.switchLang || []), defaultLanguage, 'en']));
const starlingLoadCache = new Map<string, Promise<void>>();

const normalizeLocale = (locale: string): string => locale.replace('_', '-');

export const getSupportedLocale = (locale?: string | null): string => {
  if (!locale) {
    return defaultLanguage;
  }

  const normalizedLocale = normalizeLocale(locale);
  const exactLocale = supportedLngs.find(lang => lang.toLowerCase() === normalizedLocale.toLowerCase());
  if (exactLocale) {
    return exactLocale;
  }

  const localeBase = normalizedLocale.split('-')[0].toLowerCase();
  const baseLocale = supportedLngs.find(lang => lang.toLowerCase() === localeBase);
  if (baseLocale) {
    return baseLocale;
  }

  return supportedLngs.find(lang => lang.toLowerCase().startsWith(`${localeBase}-`)) ?? defaultLanguage;
};

const isNonEmptyRecord = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === 'object' && Object.keys(value as Record<string, unknown>).length > 0;
};

const getStarlingTexts = (value: unknown): Record<string, unknown> => {
  if (!isNonEmptyRecord(value)) {
    return {};
  }

  const message = value.message;
  if (isNonEmptyRecord(message) && isNonEmptyRecord(message.Data)) {
    return message.Data;
  }

  if (isNonEmptyRecord(value.Data)) {
    return value.Data;
  }

  return value;
};

export const getUrlLocale = (): string => {
  const searchParams = new URLSearchParams(window.location.search);
  return getSupportedLocale(
    searchParams.get('lng') || searchParams.get('lang') || searchParams.get('lan') || searchParams.get('locale')
  );
};

const loadStarlingResources = async (locale: string): Promise<void> => {
  const startedAt = Date.now();
  const starling = new Starling({
    apiKey: STARLING_API_KEY,
    projectId: STARLING_PROJECT_ID,
    namespace: [STARLING_NAMESPACE],
    locale,
    zoneHost: STARLING_ZONE_HOST,
    TEAChannelDomain: STARLING_TEA_CHANNEL_DOMAIN,
    test: false,
    lazyUpdate: true
  } as any);

  try {
    const texts = await starling.load();
    const starlingTexts = getStarlingTexts(texts);
    i18n.addResourceBundle(locale, EMAIL_NAMESPACE, starlingTexts, true, true);

    const waitForRefresh = (starling as unknown as { waitForRefresh?: () => Promise<unknown> }).waitForRefresh;
    if (typeof waitForRefresh === 'function') {
      const refreshedTexts = await waitForRefresh.call(starling);
      const nextTexts = getStarlingTexts(refreshedTexts);

      if (isNonEmptyRecord(nextTexts)) {
        i18n.addResourceBundle(locale, EMAIL_NAMESPACE, nextTexts, true, true);
      }
    }
    reportDependencyResult({
      dependency: 'starling',
      stage: 'load',
      result: 'success',
      startedAt,
      locale,
      projectId: STARLING_PROJECT_ID
    });
  } catch (error) {
    reportDependencyResult({
      dependency: 'starling',
      stage: 'load',
      result: 'unexpected_error',
      startedAt,
      error,
      locale,
      projectId: STARLING_PROJECT_ID
    });
    console.error('Starling load failed', error);
  }
};

const ensureStarlingLoaded = (locale: string): Promise<void> => {
  const normalizedLocale = getSupportedLocale(locale);
  const promise = starlingLoadCache.get(normalizedLocale) ?? loadStarlingResources(normalizedLocale);

  starlingLoadCache.set(normalizedLocale, promise);
  return promise;
};

const initPromise = i18n.use(initReactI18next).init({
  lng: getUrlLocale(),
  ns: [EMAIL_NAMESPACE],
  defaultNS: EMAIL_NAMESPACE,
  resources: {},
  supportedLngs,
  nonExplicitSupportedLngs: false,
  fallbackLng: defaultLanguage,
  interpolation: {
    escapeValue: false
  },
  react: {
    bindI18n: 'languageChanged',
    bindI18nStore: 'added'
  }
});

void initPromise
  .then(() => ensureStarlingLoaded(i18n.language))
  .catch(() => {
    // ignore
  });

i18n.on('languageChanged', lng => {
  void ensureStarlingLoaded(lng).catch(() => {
    // ignore
  });
});

export default i18n;
